// Single source of truth for role -> permission mapping.
//
// The API returns a numeric `user_type` (e.g. `user_type: 1`) on the logged-in
// user object, never a role name. Everywhere in the app that needs to make an
// access decision must go through hasAccess()/getAccessLevel() below instead of
// comparing `user_type` against a raw number.
//
// TODO(roles-db-migration): ROLE_PERMISSIONS is a static object today. It can be
// moved to a DB table + a Role Management admin screen later without touching
// any call site in the app - only the body of getAccessLevel() would change to
// read from an API/cache instead of this object. USER_TYPE/ROLE/FEATURES/ACCESS
// stay as-is either way.

export const USER_TYPE = {
  1: "SUPER_ADMIN",
  3: "ADMIN",
  5: "VOLUNTEER",
  6: "VIEWER",
  2: "SUB_ADMIN", // new role - no existing API/DB value before this refactor
  4: "BOOTH_MANAGER", // new role - no existing API/DB value before this refactor
};

// Reverse lookup: role name -> numeric user_type. Add a new role by adding one
// entry to USER_TYPE above; ROLE is derived automatically.
export const ROLE = Object.fromEntries(
  Object.entries(USER_TYPE).map(([num, name]) => [name, Number(num)]),
);

export const FEATURES = {
  DASHBOARD: "DASHBOARD",
  VIEW_VOTERS: "VIEW_VOTERS",
  // Sidebar-restructure submodules of Voter Management: each starts out with
  // the exact same access level VIEW_VOTERS has for every role (see
  // ROLE_PERMISSIONS below) - a behavioral no-op today, but independently
  // gateable later without touching the other two.
  VOTER_SEARCH: "VOTER_SEARCH",
  AGE_SEARCH: "AGE_SEARCH",
  ALPHABETICAL_SEARCH: "ALPHABETICAL_SEARCH",
  ADD_VOTER: "ADD_VOTER",
  EDIT_VOTER: "EDIT_VOTER",
  DELETE_VOTER: "DELETE_VOTER",
  UPLOAD_EXCEL: "UPLOAD_EXCEL",
  BULK_UPDATE: "BULK_UPDATE",
  REPORTS: "REPORTS",
  USER_MANAGEMENT: "USER_MANAGEMENT",
  ROLE_MANAGEMENT: "ROLE_MANAGEMENT",
  SYSTEM_SETTINGS: "SYSTEM_SETTINGS",
  CAST_PROFESSION: "CAST_PROFESSION",
  BLA_BLE: "BLA_BLE",
};

export const ACCESS = {
  FULL: "full",
  LIMITED: "limited",
  VIEW_ONLY: "view_only",
  NONE: "none",
};

// Order matters: used by hasAccess() to compare "at least this level".
const ACCESS_RANK = {
  [ACCESS.NONE]: 0,
  [ACCESS.VIEW_ONLY]: 1,
  [ACCESS.LIMITED]: 2,
  [ACCESS.FULL]: 3,
};

const { FULL, LIMITED, VIEW_ONLY, NONE } = ACCESS;
const F = FEATURES;

// role -> feature -> access level.
// To add a new role: add one entry to USER_TYPE and one row here. Nothing else
// in the app needs to change.
export const ROLE_PERMISSIONS = {
  [ROLE.SUPER_ADMIN]: {
    [F.DASHBOARD]: FULL,
    [F.VIEW_VOTERS]: FULL,
    [F.VOTER_SEARCH]: FULL,
    [F.AGE_SEARCH]: FULL,
    [F.ALPHABETICAL_SEARCH]: FULL,
    [F.ADD_VOTER]: FULL,
    [F.EDIT_VOTER]: FULL,
    [F.DELETE_VOTER]: FULL,
    [F.UPLOAD_EXCEL]: FULL,
    [F.BULK_UPDATE]: FULL,
    [F.REPORTS]: FULL,
    [F.USER_MANAGEMENT]: FULL,
    [F.ROLE_MANAGEMENT]: FULL,
    [F.SYSTEM_SETTINGS]: FULL,
    [F.CAST_PROFESSION]: FULL,
    [F.BLA_BLE]: FULL,
  },
  [ROLE.SUB_ADMIN]: {
    [F.DASHBOARD]: FULL,
    [F.VIEW_VOTERS]: FULL, // ownership-scoped: only records this Sub Admin created, see isOwner()
    [F.VOTER_SEARCH]: FULL,
    [F.AGE_SEARCH]: FULL,
    [F.ALPHABETICAL_SEARCH]: FULL,
    [F.ADD_VOTER]: FULL,
    [F.EDIT_VOTER]: LIMITED, // ownership-scoped
    [F.DELETE_VOTER]: LIMITED, // ownership-scoped
    [F.UPLOAD_EXCEL]: FULL,
    [F.BULK_UPDATE]: LIMITED, // ownership-scoped
    [F.REPORTS]: LIMITED, // own data only
    [F.USER_MANAGEMENT]: LIMITED, // ownership-scoped: accounts this Sub Admin created
    [F.ROLE_MANAGEMENT]: NONE,
    [F.SYSTEM_SETTINGS]: FULL, // Application Management: full, same as Super Admin
    [F.CAST_PROFESSION]: LIMITED,
    [F.BLA_BLE]: LIMITED,
  },
  [ROLE.ADMIN]: {
    [F.DASHBOARD]: FULL,
    [F.VIEW_VOTERS]: FULL,
    [F.VOTER_SEARCH]: FULL,
    [F.AGE_SEARCH]: FULL,
    [F.ALPHABETICAL_SEARCH]: FULL,
    [F.ADD_VOTER]: FULL,
    [F.EDIT_VOTER]: FULL,
    [F.DELETE_VOTER]: NONE,
    [F.UPLOAD_EXCEL]: NONE,
    [F.BULK_UPDATE]: NONE,
    [F.REPORTS]: NONE,
    [F.USER_MANAGEMENT]: LIMITED, // ownership-scoped: self + Volunteer/Viewer/Booth Manager records
    [F.ROLE_MANAGEMENT]: NONE,
    [F.SYSTEM_SETTINGS]: LIMITED,
    [F.CAST_PROFESSION]: LIMITED,
    [F.BLA_BLE]: LIMITED,
  },
  [ROLE.BOOTH_MANAGER]: {
    [F.DASHBOARD]: FULL,
    [F.VIEW_VOTERS]: FULL,
    [F.VOTER_SEARCH]: FULL,
    [F.AGE_SEARCH]: FULL,
    [F.ALPHABETICAL_SEARCH]: FULL,
    [F.ADD_VOTER]: LIMITED, // scoped to assigned booth(s)
    [F.EDIT_VOTER]: LIMITED, // scoped to assigned booth(s)
    [F.DELETE_VOTER]: LIMITED, // scoped to assigned booth(s)
    [F.UPLOAD_EXCEL]: LIMITED, // scoped to assigned booth(s)
    [F.BULK_UPDATE]: LIMITED, // scoped to assigned booth(s)
    [F.REPORTS]: FULL,
    [F.USER_MANAGEMENT]: NONE,
    [F.ROLE_MANAGEMENT]: NONE,
    [F.SYSTEM_SETTINGS]: NONE,
    [F.CAST_PROFESSION]: LIMITED,
    [F.BLA_BLE]: LIMITED,
  },
  [ROLE.VOLUNTEER]: {
    [F.DASHBOARD]: FULL,
    [F.VIEW_VOTERS]: FULL,
    [F.VOTER_SEARCH]: FULL,
    [F.AGE_SEARCH]: FULL,
    [F.ALPHABETICAL_SEARCH]: FULL,
    [F.ADD_VOTER]: FULL,
    [F.EDIT_VOTER]: FULL,
    [F.DELETE_VOTER]: NONE,
    [F.UPLOAD_EXCEL]: NONE,
    [F.BULK_UPDATE]: NONE,
    [F.REPORTS]: NONE,
    [F.USER_MANAGEMENT]: LIMITED, // ownership-scoped: self + Viewer records
    [F.ROLE_MANAGEMENT]: NONE,
    [F.SYSTEM_SETTINGS]: NONE,
    [F.CAST_PROFESSION]: LIMITED,
    [F.BLA_BLE]: LIMITED,
  },
  [ROLE.VIEWER]: {
    [F.DASHBOARD]: FULL,
    [F.VIEW_VOTERS]: FULL,
    [F.VOTER_SEARCH]: FULL,
    [F.AGE_SEARCH]: FULL,
    [F.ALPHABETICAL_SEARCH]: FULL,
    [F.ADD_VOTER]: NONE,
    [F.EDIT_VOTER]: NONE,
    [F.DELETE_VOTER]: NONE,
    [F.UPLOAD_EXCEL]: NONE,
    [F.BULK_UPDATE]: NONE,
    [F.REPORTS]: NONE,
    [F.USER_MANAGEMENT]: LIMITED, // ownership-scoped: self only
    [F.ROLE_MANAGEMENT]: NONE,
    [F.SYSTEM_SETTINGS]: NONE,
    [F.CAST_PROFESSION]: VIEW_ONLY,
    [F.BLA_BLE]: VIEW_ONLY,
  },
};

/**
 * user_type (number) -> feature -> access level ("full" | "limited" | "view_only" | "none").
 * Returns "none" for an unknown user_type or feature instead of throwing, since
 * a role/feature that isn't in the matrix should never silently grant access.
 */
export function getAccessLevel(user_type, feature) {
  const permissions = ROLE_PERMISSIONS[Number(user_type)];
  return permissions?.[feature] ?? ACCESS.NONE;
}

/**
 * Convenience check for the common case ("does this role have at least
 * view access", "at least limited access", etc). Defaults to "any access
 * beyond none".
 */
export function hasAccess(user_type, feature, minLevel = ACCESS.VIEW_ONLY) {
  const level = getAccessLevel(user_type, feature);
  return ACCESS_RANK[level] >= ACCESS_RANK[minLevel];
}

export function getRoleName(user_type) {
  return USER_TYPE[Number(user_type)];
}

// Human-readable label shown in the UI (e.g. Header dropdown, User list).
// Derived from USER_TYPE so a new role only needs the one entry there.
export const ROLE_LABELS = Object.fromEntries(
  Object.entries(USER_TYPE).map(([num, name]) => [
    num,
    name
      .split("_")
      .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
      .join(" "),
  ]),
);

export function getRoleLabel(user_type) {
  return ROLE_LABELS[Number(user_type)] ?? "Viewer";
}

/**
 * Ownership check for roles whose matrix entry is ownership-scoped (Sub Admin,
 * Admin, Volunteer, Viewer on USER_MANAGEMENT; Sub Admin on voter features).
 * `record` is the row being acted on (must carry the id of its creator/owner
 * under one of the common field names used across the app's API responses).
 */
export function isOwner(loggedInUser, record) {
  if (!loggedInUser || !record) return false;
  const ownerId =
    record.uuid ?? record.created_by ?? record.user_id ?? record.id;
  return ownerId != null && ownerId === loggedInUser.id;
}

// Seniority order for "who can manage whom" (assignable-role dropdowns, User
// Management edit/delete). This is intentionally separate from the raw
// user_type numbers, which are just API-assigned IDs and are NOT in seniority
// order (Sub Admin=5 outranks Admin=2 despite the higher number). Adding a new
// role only means inserting it at the right position in this array.
export const ROLE_HIERARCHY = [
  ROLE.SUPER_ADMIN,
  ROLE.SUB_ADMIN,
  ROLE.ADMIN,
  ROLE.BOOTH_MANAGER,
  ROLE.VOLUNTEER,
  ROLE.VIEWER,
];

function rankOf(user_type) {
  const idx = ROLE_HIERARCHY.indexOf(Number(user_type));
  return idx === -1 ? ROLE_HIERARCHY.length : idx;
}

/**
 * Roles a given user_type is allowed to assign to a new/edited account:
 * itself and anything below it in ROLE_HIERARCHY. Replaces the old hardcoded
 * userTypeList/userTypeSuperList/volunteerUserTypeList dropdown arrays.
 */
export function getAssignableRoles(user_type) {
  const rank = rankOf(user_type);
  return ROLE_HIERARCHY.filter((_, idx) => idx >= rank).map((roleValue) => ({
    label: getRoleLabel(roleValue),
    value: roleValue,
  }));
}

/**
 * Can `loggedInUser` edit a User Management record for `targetRecord`?
 * Mirrors the previously hardcoded per-role rules in Orders.js:
 * - Super Admin: anyone
 * - Sub Admin: only accounts they created (needs a `created_by` field on the
 *   record - TODO(backend): confirm the API returns this for user records)
 * - Admin: themself, or any Volunteer/Viewer/Booth Manager record
 * - Volunteer: themself, or any Viewer record
 * - Booth Manager / Viewer: themself only
 */
export function canEditUserRecord(loggedInUser, targetRecord) {
  const actingType = Number(loggedInUser?.user_type);
  if (actingType === ROLE.SUPER_ADMIN) return true;
  if (actingType === ROLE.SUB_ADMIN) return isOwner(loggedInUser, targetRecord);
  if (actingType === ROLE.ADMIN) {
    return (
      isOwner(loggedInUser, targetRecord) ||
      [ROLE.VOLUNTEER, ROLE.VIEWER, ROLE.BOOTH_MANAGER].includes(
        Number(targetRecord?.user_type),
      )
    );
  }
  if (actingType === ROLE.VOLUNTEER) {
    return (
      isOwner(loggedInUser, targetRecord) ||
      Number(targetRecord?.user_type) === ROLE.VIEWER
    );
  }
  return false;
}

/**
 * Can `loggedInUser` delete a User Management record for `targetRecord`?
 * - Super Admin: anyone
 * - Sub Admin: only accounts they created
 * - Admin: themself, or any Volunteer/Viewer/Booth Manager record
 * - Volunteer / Booth Manager / Viewer: themself only
 */
export function canDeleteUserRecord(loggedInUser, targetRecord) {
  const actingType = Number(loggedInUser?.user_type);
  if (actingType === ROLE.SUPER_ADMIN) return true;
  if (actingType === ROLE.SUB_ADMIN) return isOwner(loggedInUser, targetRecord);
  if (actingType === ROLE.ADMIN) {
    return (
      isOwner(loggedInUser, targetRecord) ||
      [ROLE.VOLUNTEER, ROLE.VIEWER, ROLE.BOOTH_MANAGER].includes(
        Number(targetRecord?.user_type),
      )
    );
  }
  return isOwner(loggedInUser, targetRecord);
}
